import { Component, OnInit } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { SearchFieldDependency } from '../interfaces/builder-screen.models';

export interface DependencyFieldOption {
  dataPath: string;
  label: string;
  keyField: string;
  fieldType: string;
}

interface DependencyItem {
  field: DependencyFieldOption;
  checked: boolean;
  filterColumn: string;
  requiredMessage: string;
  clearOnChange: boolean;
}

interface TypeGroup {
  type: string;
  label: string;
  color: string;
  expanded: boolean;
  items: DependencyItem[];
}

@Component({
  selector: 'app-search-dependency-modal',
  templateUrl: './search-dependency-modal.component.html',
  styleUrls: ['./search-dependency-modal.component.scss'],
  standalone: false,
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ height: 0, opacity: 0, overflow: 'hidden' }))
      ])
    ])
  ]
})
export class SearchDependencyModalComponent implements OnInit {
  private static TYPE_LABELS: { [key: string]: string } = {
    search: 'Pesquisa',
    querySelect: 'Pesquisa (Query)',
    text: 'Texto',
    textarea: 'Texto Longo',
    number: 'Número',
    dropdown: 'Dropdown',
    select: 'Select',
    switch: 'Switch',
    date: 'Data',
  };

  private static TYPE_COLORS: { [key: string]: string } = {
    search: 'blue',
    querySelect: 'blue',
    text: 'green',
    textarea: 'green',
    number: 'orange',
    dropdown: 'purple',
    select: 'purple',
    switch: 'cyan',
    date: 'magenta',
  };

  availableFields: DependencyFieldOption[] = [];
  existingDependencies: SearchFieldDependency[] = [];

  items: DependencyItem[] = [];
  groups: TypeGroup[] = [];
  searchText = '';

  constructor(private modal: NzModalRef) {}

  ngOnInit(): void {
    const existingMap = new Map(
      this.existingDependencies.map(d => [d.sourceDataPath, d])
    );

    this.items = this.availableFields.map(f => {
      const existing = existingMap.get(f.dataPath);
      return {
        field: f,
        checked: !!existing,
        filterColumn: existing?.filterColumn || f.keyField || '',
        requiredMessage: existing?.requiredMessage || `Selecione ${f.label} primeiro`,
        clearOnChange: existing?.clearOnChange ?? true
      };
    });

    this.buildGroups();
  }

  private buildGroups(): void {
    const groupMap = new Map<string, DependencyItem[]>();
    for (const item of this.items) {
      const type = item.field.fieldType || 'text';
      if (!groupMap.has(type)) groupMap.set(type, []);
      groupMap.get(type)!.push(item);
    }

    this.groups = Array.from(groupMap.entries()).map(([type, items]) => ({
      type,
      label: SearchDependencyModalComponent.TYPE_LABELS[type] || type,
      color: SearchDependencyModalComponent.TYPE_COLORS[type] || 'default',
      expanded: items.some(i => i.checked) || groupMap.size <= 3,
      items
    }));
  }

  get filteredGroups(): TypeGroup[] {
    if (!this.searchText.trim()) return this.groups;
    const term = this.searchText.toLowerCase().trim();
    return this.groups
      .map(g => ({
        ...g,
        items: g.items.filter(i =>
          i.field.label.toLowerCase().includes(term) ||
          i.field.dataPath.toLowerCase().includes(term)
        )
      }))
      .filter(g => g.items.length > 0);
  }

  get selectedCount(): number {
    return this.items.filter(i => i.checked).length;
  }

  get totalVisible(): number {
    return this.filteredGroups.reduce((sum, g) => sum + g.items.length, 0);
  }

  onItemToggled(item: DependencyItem): void {
    if (item.checked) {
      if (!item.filterColumn) {
        item.filterColumn = item.field.keyField || '';
      }
      if (!item.requiredMessage) {
        item.requiredMessage = `Selecione ${item.field.label} primeiro`;
      }
    }
  }

  toggleGroup(group: TypeGroup): void {
    group.expanded = !group.expanded;
  }

  getCheckedCount(group: TypeGroup): number {
    return group.items.filter(i => i.checked).length;
  }

  handleOk(): void {
    const result: SearchFieldDependency[] = this.items
      .filter(i => i.checked)
      .map(i => {
        const dep: SearchFieldDependency = {
          sourceDataPath: i.field.dataPath,
          sourceLabel: i.field.label,
          requiredMessage: i.requiredMessage || `Selecione ${i.field.label} primeiro`,
          clearOnChange: i.clearOnChange
        };
        if (i.filterColumn?.trim()) {
          dep.filterColumn = i.filterColumn.trim();
        }
        return dep;
      });
    this.modal.close(result);
  }

  handleCancel(): void {
    this.modal.close(undefined);
  }

  trackByType(index: number, group: TypeGroup): string {
    return group.type;
  }

  trackByDataPath(index: number, item: DependencyItem): string {
    return item.field.dataPath;
  }
}
